import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ChevronRightIcon, CodeIcon, UsersIcon, BriefcaseIcon, StarIcon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, AwardIcon, SearchIcon, ClockIcon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { testimonials, developers } from '../utils/data';
import { TechnologyLinksSection } from '../components/sections/TechnologyLinksSection';
import { TalentCapsuleCard } from '../components/developers/TalentCapsuleCard';
const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState({
    stats: false
  });
  const statsRef = useRef<HTMLDivElement>(null);
  const slideTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-advance slides
  useEffect(() => {
    const startTimer = () => {
      slideTimerRef.current = setTimeout(() => {
        handleSlideChange((activeSlide + 1) % 2);
      }, 6000); // 6 seconds per slide
    };
    startTimer();
    return () => {
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
      }
    };
  }, [activeSlide]);
  const handleSlideChange = (index: number) => {
    if (index === activeSlide || isTransitioning) return;
    setIsTransitioning(true);
    setActiveSlide(index);
    // Reset transitioning state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800);
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === statsRef.current) {
            setIsVisible(prev => ({
              ...prev,
              stats: true
            }));
          }
        }
      });
    }, {
      threshold: 0.2
    });
    if (statsRef.current) observer.observe(statsRef.current);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);
  // Stats
  const stats = [{
    number: '4,500+',
    label: 'Certified Developers',
    icon: AwardIcon
  }, {
    number: '50+',
    label: 'Hiring Companies',
    icon: BriefcaseIcon
  }, {
    number: '100+',
    label: 'Job Openings',
    icon: TrendingUpIcon
  }, {
    number: '15+',
    label: 'Certifications',
    icon: ShieldCheckIcon
  }];
  return <div className="w-full overflow-hidden bg-background relative transition-colors duration-300" data-id="element-983">
      {/* Global Grain Texture - Dark mode only via mix-blend-overlay which needs dark bg,
                            or we adjust opacity for light mode. For now, keeping it subtle. */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay dark:opacity-[0.05]" data-id="element-984"></div>

      {/* Hero Slideshow Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-id="element-985">
        {/* Slide 1 - Developers */}
        <div className={`absolute inset-0 transition-opacity duration-800 ${activeSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-986">
          {/* Background: Dark gradient in dark mode, Light gradient in light mode */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-500" data-id="element-987">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-988">
              <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#d73b3e]/10 to-transparent rounded-full blur-3xl" style={{
              transform: `translateY(${scrollY * 0.3}px)`
            }} data-id="element-989" />
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full blur-3xl" style={{
              transform: `translateY(${-scrollY * 0.2}px)`
            }} data-id="element-990" />
              {/* Grid pattern overlay */}
              <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `linear-gradient(currentColor 1px, transparent 1px),
                                   linear-gradient(90deg, currentColor 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              color: 'var(--text-muted)'
            }} data-id="element-991" />
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 h-screen flex items-center" data-id="element-992">
            <div className="grid lg:grid-cols-2 gap-16 items-center w-full text-text-main" data-id="element-993">
              {/* Left content */}
              <div className={`transition-all duration-1000 ${activeSlide === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
              transitionDelay: activeSlide === 0 ? '200ms' : '0ms'
            }} data-id="element-994">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface/50 backdrop-blur-sm rounded-full border border-border mb-6 shadow-sm" data-id="element-995">
                  <TrendingUpIcon className="w-4 h-4 text-primary" data-id="element-996" />
                  <span className="text-sm font-medium text-text-main" data-id="element-997">
                    Join 4,500+ certified developers
                  </span>
                </div>

                <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight text-text-main" data-id="element-998">
                  Showcase your{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-999">
                    certified
                  </span>{' '}
                  and verifiable tech skills
                </h1>

                <p className="text-xl text-text-muted mb-8 leading-relaxed" data-id="element-1000">
                  Build your professional profile, display your certifications,
                  and connect with companies actively hiring. Stand out with
                  verified credentials.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-1001">
                  <Link to="/signup" className="group px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center" data-id="element-1002">
                    Register
                    <ChevronRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1003" />
                  </Link>
                  <Link to="/jobs" className="px-8 py-4 bg-surface/50 backdrop-blur-sm text-text-main rounded-full font-semibold text-lg hover:bg-surface transition-all duration-300 border-2 border-border flex items-center justify-center" data-id="element-1004">
                    Browse Jobs
                  </Link>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-6" data-id="element-1005">
                  <div data-id="element-1006">
                    <div className="text-3xl font-bold mb-1 text-text-main" data-id="element-1007">
                      30%
                    </div>
                    <div className="text-sm text-text-muted" data-id="element-1008">Higher rates</div>
                  </div>
                  <div data-id="element-1009">
                    <div className="text-3xl font-bold mb-1 text-text-main" data-id="element-1010">
                      7 days
                    </div>
                    <div className="text-sm text-text-muted" data-id="element-1011">Avg. to hired</div>
                  </div>
                  <div data-id="element-1012">
                    <div className="text-3xl font-bold mb-1 text-text-main" data-id="element-1013">
                      100+
                    </div>
                    <div className="text-sm text-text-muted" data-id="element-1014">Active jobs</div>
                  </div>
                </div>
              </div>

              {/* Right visual - Video showcase */}
              <div className={`relative transition-all duration-1000 ${activeSlide === 0 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{
              transitionDelay: activeSlide === 0 ? '400ms' : '0ms'
            }} data-id="element-1015">
                <div className="relative w-full max-w-lg mx-auto" data-id="element-1016">
                  {/* Video Container */}
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 dark:border-white/10 transform rotate-2 hover:rotate-0 transition-transform duration-500 bg-gray-900" data-id="element-1017">
                    <div className="aspect-video w-full" data-id="element-1018">
                      <video src="https://cdn.magicpatterns.com/uploads/hsKHrSfTuhHznpp9VrtCWc/Schermopname_2025-12-21_163931.undefined" autoPlay loop muted playsInline className="w-full h-full object-contain" data-id="element-1019" />
                    </div>

                    {/* Overlay gradient for polish */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent pointer-events-none" data-id="element-1020" />
                  </div>

                  {/* Decorative elements behind */}
                  <div className="absolute -z-10 top-10 -right-10 w-full h-full bg-gradient-to-br from-[#d73b3e]/20 to-orange-500/20 rounded-2xl blur-xl transform rotate-6" data-id="element-1021" />

                  {/* Floating badge */}
                  <div className="absolute -bottom-6 -left-6 bg-surface p-4 rounded-xl shadow-xl flex items-center gap-3 animate-bounce border border-border" style={{
                  animationDuration: '3s'
                }} data-id="element-1022">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-1023">
                      <CheckCircle2Icon className="w-6 h-6 text-green-500" data-id="element-1024" />
                    </div>
                    <div data-id="element-1025">
                      <div className="text-sm font-bold text-text-main" data-id="element-1026">
                        Verified
                      </div>
                      <div className="text-xs text-text-muted" data-id="element-1027">
                        Certificate Valid
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 2 - Recruiters */}
        <div className={`absolute inset-0 transition-opacity duration-800 ${activeSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-1028">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-500" data-id="element-1029">
            {/* Animated grid background */}
            <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-1030">
              <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(currentColor 1px, transparent 1px),
                                   linear-gradient(90deg, currentColor 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              color: 'var(--text-muted)',
              transform: `translateY(${scrollY * 0.1}px)`
            }} data-id="element-1031" />
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 h-screen flex items-center" data-id="element-1032">
            <div className="grid lg:grid-cols-2 gap-16 items-center w-full text-text-main" data-id="element-1033">
              {/* Left visual - Technology logos showcase */}
              <div className={`relative transition-all duration-1000 ${activeSlide === 1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{
              transitionDelay: activeSlide === 1 ? '200ms' : '0ms'
            }} data-id="element-1034">
                <div className="relative" data-id="element-1035">
                  {/* Technology logos grid */}
                  <div className="grid grid-cols-3 gap-4 max-w-md" data-id="element-1036">
                    {/* Row 1 */}
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.1s both' : 'none'
                  }} data-id="element-1037">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/9a/Laravel.svg" alt="Laravel" className="w-12 h-12 object-contain" data-id="element-1038" />
                    </div>
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.2s both' : 'none'
                  }} data-id="element-1039">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg" alt="React" className="w-12 h-12 object-contain" data-id="element-1040" />
                    </div>
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.3s both' : 'none'
                  }} data-id="element-1041">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg" alt="Vue.js" className="w-12 h-12 object-contain" data-id="element-1042" />
                    </div>

                    {/* Row 2 */}
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.4s both' : 'none'
                  }} data-id="element-1043">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/2/27/PHP-logo.svg" alt="PHP" className="w-16 h-10 object-contain" data-id="element-1044" />
                    </div>
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.5s both' : 'none'
                  }} data-id="element-1045">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/4/4c/Typescript_logo_2020.svg" alt="TypeScript" className="w-12 h-12 object-contain" data-id="element-1046" />
                    </div>
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.6s both' : 'none'
                  }} data-id="element-1047">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg" alt="Node.js" className="w-12 h-12 object-contain" data-id="element-1048" />
                    </div>

                    {/* Row 3 */}
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.7s both' : 'none'
                  }} data-id="element-1049">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/d/d5/Tailwind_CSS_Logo.svg" alt="Tailwind CSS" className="w-12 h-12 object-contain" data-id="element-1050" />
                    </div>
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.8s both' : 'none'
                  }} data-id="element-1051">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/3/3f/Git_icon.svg" alt="Git" className="w-12 h-12 object-contain" data-id="element-1052" />
                    </div>
                    <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border flex items-center justify-center hover:bg-surface transition-all duration-300 shadow-sm" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.9s both' : 'none'
                  }} data-id="element-1053">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" alt="AWS" className="w-12 h-12 object-contain" data-id="element-1054" />
                    </div>
                  </div>

                  {/* Floating stats badge */}
                  <div className="absolute -top-8 -right-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 shadow-2xl" data-id="element-1055">
                    <div className="text-2xl font-bold text-white" data-id="element-1056">92%</div>
                    <div className="text-xs opacity-90 text-white" data-id="element-1057">
                      Match rate
                    </div>
                  </div>

                  {/* Additional floating badge */}
                  <div className="absolute -bottom-6 left-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-3 shadow-2xl" data-id="element-1058">
                    <div className="text-lg font-bold text-white" data-id="element-1059">4,500+</div>
                    <div className="text-xs opacity-90 text-white" data-id="element-1060">
                      Developers
                    </div>
                  </div>
                </div>
              </div>

              {/* Right content */}
              <div className={`transition-all duration-1000 ${activeSlide === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
              transitionDelay: activeSlide === 1 ? '400ms' : '0ms'
            }} data-id="element-1061">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface/50 backdrop-blur-sm rounded-full border border-border mb-6 shadow-sm" data-id="element-1062">
                  <ZapIcon className="w-4 h-4 text-primary" data-id="element-1063" />
                  <span className="text-sm font-medium text-text-main" data-id="element-1064">
                    Trusted by 500+ companies
                  </span>
                </div>

                <h2 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight text-text-main" data-id="element-1065">
                  Recruit the best{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1066">
                    tech talent
                  </span>{' '}
                  with verified certifications
                </h2>

                <p className="text-xl text-text-muted mb-8 leading-relaxed" data-id="element-1067">
                  Access 4,500+ certified developers with proven skills. Filter
                  by expertise, availability, and rate. Hire with confidence.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-1068">
                  <Link to="/developers" className="group px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center" data-id="element-1069">
                    Browse Certified Devs
                    <SearchIcon className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" data-id="element-1070" />
                  </Link>
                  <Link to="/post-job" className="px-8 py-4 bg-surface/50 backdrop-blur-sm text-text-main rounded-full font-semibold text-lg hover:bg-surface transition-all duration-300 border-2 border-border flex items-center justify-center" data-id="element-1071">
                    Post a job
                  </Link>
                </div>

                {/* Technology expertise callout */}
                <div className="bg-surface/50 backdrop-blur-sm rounded-2xl p-6 border border-border" data-id="element-1072">
                  <h3 className="text-sm font-semibold text-text-muted mb-3" data-id="element-1073">
                    Find experts in:
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1074">
                    <span className="text-xs bg-surface text-text-main border border-border px-3 py-1.5 rounded-full font-medium" data-id="element-1075">
                      Laravel
                    </span>
                    <span className="text-xs bg-surface text-text-main border border-border px-3 py-1.5 rounded-full font-medium" data-id="element-1076">
                      React
                    </span>
                    <span className="text-xs bg-surface text-text-main border border-border px-3 py-1.5 rounded-full font-medium" data-id="element-1077">
                      Vue.js
                    </span>
                    <span className="text-xs bg-surface text-text-main border border-border px-3 py-1.5 rounded-full font-medium" data-id="element-1078">
                      TypeScript
                    </span>
                    <span className="text-xs bg-surface text-text-main border border-border px-3 py-1.5 rounded-full font-medium" data-id="element-1079">
                      Node.js
                    </span>
                    <span className="text-xs bg-surface text-text-main border border-border px-3 py-1.5 rounded-full font-medium" data-id="element-1080">
                      +20 more
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Navigation Dots */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex gap-3" data-id="element-1081">
          {[0, 1].map(index => <button key={index} onClick={() => handleSlideChange(index)} className={`transition-all duration-300 rounded-full ${activeSlide === index ? 'w-12 h-3 bg-primary' : 'w-3 h-3 bg-text-muted/40 hover:bg-text-muted/60'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-1082" />)}
        </div>
      </section>

      {/* Technology Links Section */}
      <section className="bg-surface py-20 border-t border-border" data-id="element-1083">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1084">
          <div className="text-center mb-12" data-id="element-1085">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-text-main" data-id="element-1086">
              Browse by{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1087">
                Technology
              </span>
            </h2>
            <p className="text-lg text-text-muted" data-id="element-1088">
              Find developers by their technology expertise
            </p>
          </div>
          <TechnologyLinksSection data-id="element-1089" />
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-background" data-id="element-1090">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1091">
          <div className="text-center mb-12" data-id="element-1092">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight text-text-main" data-id="element-1093">
              Our numbers{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1094">
                speak
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1095">
            {stats.map((stat, index) => {
            const Icon = stat.icon;
            return <div key={index} className={`text-center transition-all duration-700 ${isVisible.stats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
              transitionDelay: `${index * 100}ms`
            }} data-id="element-1096">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl mb-4 border border-border" data-id="element-1097">
                    <Icon className="w-8 h-8 text-primary" data-id="element-1098" />
                  </div>
                  <div className="text-4xl font-bold text-text-main mb-2" data-id="element-1099">
                    {stat.number}
                  </div>
                  <div className="text-sm font-medium text-text-muted" data-id="element-1100">
                    {stat.label}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Developers Section */}
      <section className="py-24 bg-gradient-to-br from-surface to-background relative overflow-hidden" data-id="element-1101">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5" data-id="element-1102">
          <div className="absolute top-20 right-0 w-96 h-96 bg-[#d73b3e] rounded-full blur-3xl" data-id="element-1103"></div>
          <div className="absolute bottom-20 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl" data-id="element-1104"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1105">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1106">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 backdrop-blur-sm rounded-full border border-primary/20 mb-6" data-id="element-1107">
              <StarIcon className="w-4 h-4 text-primary" data-id="element-1108" />
              <span className="text-sm font-medium text-primary" data-id="element-1109">
                Top Talent
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-text-main mb-6 leading-tight" data-id="element-1110">
              Meet our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1111">
                certified developers
              </span>
            </h2>
            <p className="text-lg text-text-muted leading-relaxed" data-id="element-1112">
              Handpicked professionals with verified skills and proven track
              records. Ready to join your team today.
            </p>
          </div>

          {/* Featured Developer Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-1113">
            {developers.slice(0, 6).map((developer, index) => <div key={developer.id} className="opacity-0 animate-fade-in" style={{
            animation: `fadeInUp 0.6s ease-out ${index * 0.1}s forwards`
          }} data-id="element-1114">
                <TalentCapsuleCard developer={developer} data-id="element-1115" />
              </div>)}
          </div>

          {/* CTA */}
          <div className="text-center" data-id="element-1116">
            <Link to="/developers" className="inline-flex items-center gap-3 px-8 py-4 bg-surface/50 backdrop-blur-sm text-text-main rounded-full font-semibold text-lg hover:bg-surface transition-all duration-300 border-2 border-border hover:border-primary group" data-id="element-1117">
              View all {developers.length} certified developers
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1118" />
            </Link>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 bg-background border-t border-border" data-id="element-1119">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1120">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1121">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-text-main" data-id="element-1122">
              Why choose{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1123">
                CertifiedDevs?
              </span>
            </h2>
            <p className="text-lg text-text-muted" data-id="element-1124">
              The trusted platform for certified talent
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-1125">
            <div className="bg-surface p-8 rounded-2xl shadow-lg border border-border hover:border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-id="element-1126">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6 border border-border" data-id="element-1127">
                <CodeIcon className="h-7 w-7 text-primary" data-id="element-1128" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-main" data-id="element-1129">
                Vetted Developers
              </h3>
              <p className="text-text-muted leading-relaxed" data-id="element-1130">
                All developers are manually reviewed and verified to ensure
                quality.
              </p>
            </div>
            <div className="bg-surface p-8 rounded-2xl shadow-lg border border-border hover:border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-id="element-1131">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6 border border-border" data-id="element-1132">
                <ShieldCheckIcon className="h-7 w-7 text-primary" data-id="element-1133" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-main" data-id="element-1134">
                Certified Expertise
              </h3>
              <p className="text-text-muted leading-relaxed" data-id="element-1135">
                Industry-recognized certifications that validate real skills and
                knowledge.
              </p>
            </div>
            <div className="bg-surface p-8 rounded-2xl shadow-lg border border-border hover:border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-id="element-1136">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6 border border-border" data-id="element-1137">
                <ZapIcon className="h-7 w-7 text-primary" data-id="element-1138" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-text-main" data-id="element-1139">
                Fast Matching
              </h3>
              <p className="text-text-muted leading-relaxed" data-id="element-1140">
                Advanced filters and search to find the perfect match in days,
                not weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection testimonials={testimonials} data-id="element-1141" />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white border-t border-border" data-id="element-1142">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1143">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1144">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-1145">
              Ready to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1146">
                get started?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-10" data-id="element-1147">
              Join thousands of developers and companies already using
              CertifiedDevs
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1148">
              <Link to="/signup" className="px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" data-id="element-1149">
                Get Certified
              </Link>
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-1150">
                Browse Developers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;